import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { ArrowLeft, Save, Upload, X, Eye, EyeOff } from 'lucide-react';
import { Form, Button, Card, Container, Row, Col, Image, InputGroup } from 'react-bootstrap';
import { useAlert } from '../context/AlertContext';

const UserForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showAlert } = useAlert();
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
    });
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [currentPhoto, setCurrentPhoto] = useState<string | null>(null);
    const [removePhotoFlag, setRemovePhotoFlag] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (id) {
            fetchUser();
        }
    }, [id]);

    const fetchUser = async () => {
        try {
            const response = await api.get(`/company/users/${id}/`);
            // Avoid setting password in form when editing
            const { password, profile, photo, ...userData } = response.data;
            setFormData({ ...userData, password: '' }); 
            if (photo) {
                setCurrentPhoto(photo);
            }
        } catch (error) {
            console.error('Error fetching user:', error);
            showAlert('Erro ao carregar dados do usuário.', { type: 'error' });
            navigate('/users');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setPhotoFile(null);
        setPhotoPreview(null);
        if (currentPhoto) {
            setRemovePhotoFlag(true);
            setCurrentPhoto(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const submitData = new FormData();
            submitData.append('username', formData.username);
            submitData.append('email', formData.email);
            submitData.append('first_name', formData.first_name);
            submitData.append('last_name', formData.last_name);
            
            if (formData.password) {
                submitData.append('password', formData.password);
            }

            if (photoFile) {
                submitData.append('photo', photoFile);
            } else if (removePhotoFlag) {
                submitData.append('photo', ''); // Signal to backend to clear photo if supported
            }

            const headers = { 'Content-Type': 'multipart/form-data' };

            if (id) {
                await api.patch(`/company/users/${id}/`, submitData, { headers });
                showAlert('Usuário atualizado com sucesso!', { type: 'success' });
            } else {
                await api.post('/company/users/', submitData, { headers });
                showAlert('Usuário criado com sucesso!', { type: 'success' });
            }
            navigate('/users');
        } catch (error: any) {
            console.error('Error saving user:', error);
            const errorMsg = error.response?.data?.detail || 
                             Object.values(error.response?.data || {}).flat().join(', ') ||
                             'Erro ao salvar usuário.';
            showAlert(errorMsg, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const displayPhoto = photoPreview || (currentPhoto ? `${currentPhoto}` : null);

    return (
        <Container className="py-2">
            <div className="d-flex align-items-center mb-4">
                <Button
                    variant="link"
                    onClick={() => navigate('/users')}
                    className="text-decoration-none text-secondary p-0 me-3 d-flex align-items-center gap-2"
                >
                    <ArrowLeft size={20} /> Voltar
                </Button>
                <h2 className="mb-0">{id ? 'Editar Usuário' : 'Novo Usuário'}</h2>
            </div>

            <Card className="shadow-sm border-0">
                <Card.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={8}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nome</Form.Label>
                                            <Form.Control
                                                name="first_name"
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                required
                                                placeholder="Primeiro nome"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Sobrenome</Form.Label>
                                            <Form.Control
                                                name="last_name"
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                required
                                                placeholder="Sobrenome"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Nome de Usuário (Login)</Form.Label>
                                    <Form.Control
                                        name="username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        required
                                        placeholder="Ex: joao.silva"
                                        disabled={!!id} 
                                    />
                                    {id && <Form.Text className="text-muted">O nome de usuário não pode ser alterado.</Form.Text>}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>E-mail</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        placeholder="joao@empresa.com"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>{id ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required={!id}
                                            minLength={6}
                                            placeholder={id ? "••••••" : "Mínimo 6 caracteres"}
                                        />
                                        <Button 
                                            variant="outline-secondary"
                                            onClick={() => setShowPassword(!showPassword)}
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </Button>
                                    </InputGroup>
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Foto de Perfil</Form.Label>
                                    <div className="text-center">
                                        {displayPhoto ? (
                                            <div
                                                className="position-relative d-inline-block"
                                                onClick={() => fileInputRef.current?.click()}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Image
                                                    src={displayPhoto}
                                                    alt="Preview"
                                                    roundedCircle
                                                    style={{
                                                        width: '150px',
                                                        height: '150px',
                                                        objectFit: 'cover',
                                                        border: '3px solid #dee2e6'
                                                    }}
                                                />
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    className="position-absolute top-0 end-0 rounded-circle p-1"
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        removePhoto();
                                                    }}
                                                    style={{ width: '30px', height: '30px' }}
                                                >
                                                    <X size={16} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div
                                                className="d-flex align-items-center justify-content-center bg-light rounded-circle mx-auto"
                                                onClick={() => fileInputRef.current?.click()}
                                                style={{
                                                    width: '150px',
                                                    height: '150px',
                                                    border: '2px dashed #dee2e6',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <Upload size={40} className="text-muted" />
                                            </div>
                                        )}
                                        <Form.Control
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handlePhotoChange}
                                            style={{ display: 'none' }}
                                        />
                                        <Form.Text className="text-muted d-block mt-2">
                                            Clique na imagem para alterar • JPG, PNG
                                        </Form.Text>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loading}
                                className="d-flex align-items-center gap-2 px-4"
                            >
                                <Save size={18} /> {loading ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default UserForm;
